/**
 * Shell v2 — application frame:
 * sidebar (sections, collapse) + topbar (breadcrumbs, search, actions)
 * + Cmd+K command palette + mobile drawer.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, List, BarChart3, MousePointer, DollarSign, Wrench, Users,
  LogOut, Menu, X, ChevronRight, Search, RefreshCw, Sun, Moon, Shield,
  PanelLeftClose, PanelLeft, FolderTree, Settings2, MoreHorizontal,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { reloadCache } from '../services/api';
import CommandPalette from './CommandPalette';
import './shell.css';

const ROUTE_META = [
  { match: /^\/$/, section: 'Monitor', page: 'Dashboard' },
  { match: /^\/campaigns\/new$/, section: 'Manage', page: 'Campaigns', sub: 'New campaign', to: '/campaigns' },
  { match: /^\/campaigns\/\d+\/advanced-rules$/, section: 'Manage', page: 'Campaigns', sub: 'Advanced rules', to: '/campaigns' },
  { match: /^\/campaigns\/\d+$/, section: 'Manage', page: 'Campaigns', sub: 'Details', to: '/campaigns' },
  { match: /^\/campaigns$/, section: 'Manage', page: 'Campaigns' },
  { match: /^\/stats$/, section: 'Monitor', page: 'Stats & Reports' },
  { match: /^\/clicks$/, section: 'Monitor', page: 'Click Logs' },
  { match: /^\/conversions$/, section: 'Monitor', page: 'Conversions' },
  { match: /^\/tools\/([a-z-]+)$/, section: 'Configure', page: 'Tools', dynamicSub: true, to: '/tools' },
  { match: /^\/tools$/, section: 'Configure', page: 'Tools' },
  { match: /^\/users$/, section: 'Admin', page: 'Users' },
];

const TOOL_LABELS = {
  'variants': 'Variant Lists',
  'ip-lists': 'IP Lists',
  'country-groups': 'Country Groups',
  'content-templates': 'Content Templates',
  'campaign-groups': 'Campaign Groups',
  'export-import': 'Export / Import',
  'postback-profiles': 'Postback Profiles',
  'exchange-rates': 'Exchange Rates',
  'forces': 'Forces',
  'ab-tests': 'A/B Tests',
  'ip-whitelist': 'IP Whitelist',
  'api-keys': 'API Keys',
};

export default function Shell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const toast = useToast();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('tds-sidebar-collapsed') === '1');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('tds-theme') || 'dark');
  const [cacheBusy, setCacheBusy] = useState(false);
  const [license, setLicense] = useState(null);
  const [userMenu, setUserMenu] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('tds-theme', theme);
  }, [theme]);

  useEffect(() => {
    const onTheme = (e) => setTheme(e.detail === 'light' ? 'light' : 'dark');
    window.addEventListener('tds-theme-change', onTheme);
    return () => window.removeEventListener('tds-theme-change', onTheme);
  }, []);

  useEffect(() => {
    fetch('/api/license/status')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setLicense(d))
      .catch(() => {});
  }, []);

  // Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); setUserMenu(false); }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      localStorage.setItem('tds-sidebar-collapsed', v ? '0' : '1');
      return !v;
    });
  };

  const handleCache = async () => {
    setCacheBusy(true);
    try {
      await reloadCache();
      toast.success('Campaign cache reloaded');
    } catch (e) {
      toast.error('Cache: ' + (e.response?.data?.error || e.message));
    } finally {
      setCacheBusy(false);
    }
  };

  const crumbs = useMemo(() => {
    const path = location.pathname;
    const meta = ROUTE_META.find((m) => m.match.test(path));
    if (!meta) return [{ label: 'Panel' }];
    const items = [{ label: meta.section }];
    if (meta.dynamicSub) {
      const key = path.match(/^\/tools\/([a-z-]+)/)?.[1];
      items.push({ label: meta.page, to: meta.to });
      items.push({ label: TOOL_LABELS[key] || key });
      return items;
    }
    if (meta.sub) {
      items.push({ label: meta.page, to: meta.to });
      items.push({ label: meta.sub });
      return items;
    }
    items.push({ label: meta.page });
    return items;
  }, [location.pathname]);

  const nav = [
    {
      label: 'Monitor',
      items: [
        { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
        { to: '/stats', icon: BarChart3, label: 'Stats' },
        { to: '/clicks', icon: MousePointer, label: 'Click Logs' },
        { to: '/conversions', icon: DollarSign, label: 'Conversions' },
      ],
    },
    {
      label: 'Manage',
      items: [
        { to: '/campaigns', icon: List, label: 'Campaigns' },
        { to: '/tools/campaign-groups', icon: FolderTree, label: 'Groups' },
        { to: '/tools', icon: Wrench, label: 'Tools', end: true },
      ],
    },
    ...(isAdmin
      ? [{ label: 'Admin', items: [{ to: '/users', icon: Users, label: 'Users' }] }]
      : []),
  ];

  const licensePill = (() => {
    if (!license) return null;
    const plan = (license.plan || 'community').toUpperCase();
    const never = license.expires_at?.startsWith('2099');
    const bad = license.status && license.status !== 'active';
    return (
      <div className={`v2-license ${bad ? 'is-bad' : ''}`} title={`Licencja: ${plan}${license.domain ? ' · ' + license.domain : ''}`}>
        <span className="v2-license__dot" />
        <span className="v2-license__plan">{plan}</span>
        {!collapsed && (
          <span className="v2-license__exp">{never ? '∞' : (license.expires_at || '').slice(0, 10)}</span>
        )}
      </div>
    );
  })();

  return (
    <div className={`v2-shell ${collapsed ? 'is-collapsed' : ''}`}>
      {/* Mobile top bar */}
      <header className="v2-mobilebar">
        <button className="v2-btn v2-btn--ghost v2-btn--icon" onClick={() => setMobileOpen(true)} aria-label="Menu">
          <Menu size={18} />
        </button>
        <span className="v2-mobilebar__title">TDS</span>
        <button className="v2-btn v2-btn--ghost v2-btn--icon" onClick={() => setPaletteOpen(true)} aria-label="Search">
          <Search size={17} />
        </button>
      </header>

      {/* Sidebar */}
      <aside className={`v2-sidebar ${mobileOpen ? 'is-open' : ''}`}>
        <div className="v2-sidebar__head">
          <button className="v2-logo" onClick={() => navigate('/')} title="TDS Panel">
            <span className="v2-logo__mark">T</span>
            {!collapsed && (
              <span className="v2-logo__text">
                TDS<em>panel</em>
              </span>
            )}
          </button>
          <button className="v2-btn v2-btn--ghost v2-btn--icon v2-collapse" onClick={toggleCollapsed} title="Collapse">
            {collapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
          </button>
          <button className="v2-btn v2-btn--ghost v2-btn--icon v2-mobile-close" onClick={() => setMobileOpen(false)} title="Zamknij">
            <X size={17} />
          </button>
        </div>

        <nav className="v2-sidebar__nav">
          {nav.map((group) => (
            <div className="v2-navgroup" key={group.label}>
              {!collapsed && <div className="v2-navgroup__label">{group.label}</div>}
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `v2-navlink ${isActive ? 'is-active' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={17} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="v2-sidebar__foot">
          {licensePill}
          <div className="v2-sidebar__actions">
            <button className="v2-btn v2-btn--ghost v2-btn--icon v2-btn--sm" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} title="Theme">
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button className="v2-btn v2-btn--ghost v2-btn--icon v2-btn--sm" onClick={handleCache} title="Reload cache">
              <RefreshCw size={15} className={cacheBusy ? 'is-spinning' : ''} />
            </button>
            <span className="v2-spacer" />
          </div>
          <button className="v2-usermenu-trigger" onClick={() => setUserMenu((v) => !v)}>
            <span className="v2-avatar">{(user?.name || user?.email || '?').slice(0, 1).toUpperCase()}</span>
            {!collapsed && (
              <span className="v2-usermenu__meta">
                <span className="v2-usermenu__name">{user?.name || user?.email}</span>
                <span className="v2-usermenu__role">
                  {isAdmin && <Shield size={10} />} {user?.role || ''}
                </span>
              </span>
            )}
            {!collapsed && <MoreHorizontal size={15} className="v2-dim" />}
          </button>
          {userMenu && (
            <div className="v2-menu v2-usermenu-menu">
              <div className="v2-menu__label">{user?.email}</div>
              <button className="v2-menu__item" onClick={() => navigate('/tools')}>
                <Settings2 size={14} /> Settings
              </button>
              <div className="v2-menu__sep" />
              <button className="v2-menu__item v2-menu__item--danger" onClick={() => { logout(); navigate('/login'); }}>
                <LogOut size={14} /> Log out
              </button>
            </div>
          )}
        </div>
      </aside>

      {mobileOpen && <div className="v2-sidebar-scrim" onClick={() => setMobileOpen(false)} />}

      {/* Main column */}
      <div className="v2-main">
        <header className="v2-topbar">
          <div className="v2-crumbs">
            {crumbs.map((c, i) => (
              <span key={i} className="v2-crumbs__item">
                {i > 0 && <ChevronRight size={13} className="v2-crumbs__sep" />}
                {c.to ? (
                  <button className="v2-crumbs__link" onClick={() => navigate(c.to)}>{c.label}</button>
                ) : (
                  <span className={i === crumbs.length - 1 ? 'v2-crumbs__current' : ''}>{c.label}</span>
                )}
              </span>
            ))}
          </div>
          <button className="v2-searchbtn" onClick={() => setPaletteOpen(true)} title="Szukaj (Cmd+K)">
            <Search size={14} />
            <span>Search…</span>
            <span className="v2-kbd">⌘K</span>
          </button>
        </header>

        <main className="v2-content">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
